import React from 'react';
import { usePeople } from '../context/PeopleContext';

export const PERSON_COLORS = {
  'Jonas Brothers': '#e6edf3',
  NJ:      '#3B82F6',
  K2:      '#bc8cff',
  JJ:      '#ff7b72',
  Spencer: '#3fb950',
  Chris:   '#e3b341',
  Steve:   '#ffa657',
};

export function fmt12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

export function fmtDate(d, opts) {
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', opts || {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });
}

export function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

// Card
export function Card({ children, style, className, onClick }) {
  return (
    <div
      onClick={onClick}
      className={className}
      style={{
        background: 'var(--card)',
        borderRadius: 12,
        border: '1px solid var(--border)',
        ...(onClick ? { cursor: 'pointer' } : {}),
        ...style,
      }}>
      {children}
    </div>
  );
}

// Person badge
function getInitials(name) {
  if (!name) return "";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

export function PersonBadge({ person, size = 'sm' }) {
  const { colorMap } = usePeople();
  const color = colorMap[person] || PERSON_COLORS[person] || '#e6edf3';
  const isW   = color === '#e6edf3';
  const pad   = size === 'sm' ? '3px 8px' : '5px 12px';
  const fs    = size === 'sm' ? 11 : 13;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: pad, borderRadius: 20, fontSize: fs, fontWeight: 700,
      background: isW ? 'rgba(230,237,243,0.08)' : color + '18',
      border: `1px solid ${isW ? 'var(--border)' : color + '50'}`,
      color: isW ? 'var(--text2)' : color,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: isW ? 'rgba(230,237,243,0.4)' : color, display: 'inline-block' }} />
      {getInitials(person)}
    </span>
  );
}

// Status pill
export function StatusPill({ status }) {
  const pub = status === 'published';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: pub ? 'rgba(63,185,80,0.12)' : 'rgba(227,179,65,0.12)',
      border: `1px solid ${pub ? 'rgba(63,185,80,0.4)' : 'rgba(227,179,65,0.4)'}`,
      color: pub ? 'var(--green)' : 'var(--yellow)',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: pub ? 'var(--green)' : 'var(--yellow)', display: 'inline-block' }} />
      {pub ? 'Published' : 'Draft'}
    </span>
  );
}

// Button
export function Btn({ children, onClick, variant = 'primary', size = 'md', disabled, style, type = 'button' }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    fontWeight: 700, borderRadius: 10, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'opacity 0.15s', opacity: disabled ? 0.5 : 1, fontFamily: 'inherit',
    fontSize: size === 'sm' ? 12 : size === 'lg' ? 16 : 14,
    padding: size === 'sm' ? '6px 12px' : size === 'lg' ? '14px 24px' : '9px 16px',
  };
  const variants = {
    primary:  { background: 'var(--blue)',  color: 'white' },
    success:  { background: 'var(--green)', color: 'white' },
    ghost:    { background: 'rgba(255,255,255,0.08)', color: 'var(--text)', border: '1px solid var(--border)' },
    danger:   { background: 'rgba(248,81,73,0.15)', color: 'var(--red)', border: '1px solid rgba(248,81,73,0.3)' },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{ ...base, ...variants[variant], ...style }}>
      {children}
    </button>
  );
}

// Section label
export function SLabel({ children, color }) {
  return (
    <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '2px', color: color || 'var(--text3)', marginBottom: 10, textTransform: 'uppercase' }}>
      {children}
    </p>
  );
}

// Rich text (renders **bold**)
export function RichText({ text, style }) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <span style={style}>
      {parts.map((p, i) =>
        p.startsWith('**') && p.endsWith('**')
          ? <strong key={i} style={{ fontWeight: 800, color: 'var(--text)' }}>{p.slice(2,-2)}</strong>
          : <span key={i}>{p}</span>
      )}
    </span>
  );
}

// Weather icon
export function WeatherIcon({ condition = '' }) {
  const c = condition.toLowerCase();
  if (c.includes('snow'))   return '❄️';
  if (c.includes('storm'))  return '⛈️';
  if (c.includes('rain'))   return '🌧️';
  if (c.includes('cloud'))  return '☁️';
  if (c.includes('partly')) return '⛅';
  return '☀️';
}

// Loading spinner
export function Spinner({ size = 20 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      border: `2px solid var(--border)`,
      borderTopColor: 'var(--blue)',
      animation: 'spin 0.7s linear infinite',
    }} />
  );
}
